import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { subscriptionService } from '../../../services/subscriptionService';

const BillingHistory = ({ invoices, onRefresh }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'text-green-600 bg-green-100';
      case 'open':
        return 'text-blue-600 bg-blue-100';
      case 'void': case'uncollectible':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDownload = (invoice) => {
    if (invoice?.invoicePdf) {
      window.open(invoice?.invoicePdf, '_blank');
    } else if (invoice?.hostedInvoiceUrl) {
      window.open(invoice?.hostedInvoiceUrl, '_blank');
    }
  };

  if (!invoices || invoices?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
          <Icon name="Receipt" size={32} className="text-muted-foreground" />
        </div>
        <h3 className="text-xl font-semibold mb-2">No Invoices Yet</h3>
        <p className="text-muted-foreground mb-6">
          Your billing history will appear here once you have an active subscription
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Billing History</h2>
          <p className="text-muted-foreground">View and download your invoices</p>
        </div>
        <Button 
          onClick={onRefresh}
          iconName="RefreshCw"
          iconPosition="left"
          variant="outline"
        >
          Refresh
        </Button>
      </div>

      {/* Invoices Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted border-b border-border">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Invoice
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Period
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices?.map((invoice) => (
                <tr key={invoice?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Icon name="FileText" size={18} className="text-muted-foreground" />
                      <span className="font-medium">
                        {invoice?.invoiceNumber || `INV-${invoice?.id?.slice(0, 8)}`}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {formatDate(invoice?.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="font-medium">
                        {subscriptionService?.formatAmount(invoice?.amountDue, invoice?.currency)}
                      </div>
                      {invoice?.amountPaid > 0 && invoice?.amountPaid !== invoice?.amountDue && (
                        <div className="text-xs text-muted-foreground">
                          Paid: {subscriptionService?.formatAmount(invoice?.amountPaid, invoice?.currency)}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(invoice?.status)}`}>
                      {invoice?.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {formatDate(invoice?.periodStart)} - {formatDate(invoice?.periodEnd)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-2">
                      {(invoice?.invoicePdf || invoice?.hostedInvoiceUrl) && (
                        <Button
                          onClick={() => handleDownload(invoice)}
                          iconName="Download"
                          variant="ghost"
                          size="sm"
                        >
                          Download
                        </Button>
                      )}
                      {invoice?.hostedInvoiceUrl && (
                        <Button
                          onClick={() => window.open(invoice?.hostedInvoiceUrl, '_blank')}
                          iconName="ExternalLink"
                          variant="ghost"
                          size="sm"
                        >
                          View
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-muted/50 border border-border rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Invoices</p>
            <p className="text-2xl font-bold">{invoices?.length}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Paid Invoices</p>
            <p className="text-2xl font-bold text-green-600">
              {invoices?.filter(inv => inv?.status === 'paid')?.length}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Total Paid</p>
            <p className="text-2xl font-bold">
              {subscriptionService?.formatAmount(
                invoices?.reduce((sum, inv) => sum + (inv?.amountPaid || 0), 0),
                invoices?.[0]?.currency || 'USD'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingHistory;